// crossing-route.ts — MV5 (2026-10-03, R-008 rung `creature-space`): the legacy walk's ROUTER, twinned for the walk a body sits on.
//
// The real-PHP oracle (tools/test/janus-mv4-crossing-staging-smoke.mjs, MV4) showed the legacy endpoint never walks a posted flat route: it
// routes origin → destination itself with ft_route_encounter_move() (includes/functions/janus/encounter/ft_ground_occupancy.php) — A* on
// the mover's floor band, every other creature's disc weighted ×2 (owner D2), corpses and solid props hard — and the per-foot loop prices
// THAT path. A posted route through an ally therefore landed the same spot on a different bank in the browser. This file is the router's
// twin over the same golden geometry (ft3d-move-core.js routeBetween = ft_move_route_between, parity-tested), so the browser walks the path
// PHP would: plain-walk-scope.ts re-plans a crossing walk with it and judges the re-planned route through every gate again.
//
// Exact for: the floor-band structural shape (floorBandShape + withExteriorOpenGround — lift-occupancy.ts, oracle-tested twins) or the
// bounded open plane of a shapeless encounter, creature discs (dead ones included — PHP weights them too), difficult-terrain footprints,
// a mesh body's navigation shape + height, the 5 ft → 2.5 ft retry.
// MV6 (2026-10-03): PHP's HARD obstacle set is twinned too ($hard_obstacles + $passable_at): a corpse taller than the mover's step
// (ft3d_corpse_support_rows), every solid prop as its disc / mesh footprint / tree trunk / painted-wall parts (walk-route-obstacles.ts builds
// them from the rows), with the "a mover that STARTS inside a hard disc may walk out, never deeper" rule. MV5's conservative hand-backs
// (`crossing-corpse`, `crossing-prop-footprint`, `crossing-prop`) are gone; the caller hands back only what the page cannot establish.
import { circleOccupancy, occupancyOverlap, routeBetween } from '../../../../ft3d/engine/ft3d-move-core.js';
import type { Zone } from '../inventory/floor-move.js';
/** ft3d-move-core.js ROUTE_NO_PATH (= PHP FT_ROUTE_NO_PATH). */
const ROUTE_NO_PATH = 'no_path';
import { floorBandShape, withExteriorOpenGround } from '../inventory/lift-occupancy.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body.js';
import { movementFootprint, movementOverlap } from './body-geometry.js';
import { routeWallReader } from './route-walls.js';
import { stepIsDifficultTerrain, movementClimateGround, type DifficultTerrainClimateContext } from './difficult-terrain.js';
import type { RouteWallWorld } from './route-walls.js';
import type { RestShape } from '../inventory/floor-move.js';

export interface CrossingRouteBody {
  /** ft_ground_collision_diameter_ft() / 2. */
  radius: number;
  diameter?: number;
  widthFt?: number;
  /** ft_creature_physical_mesh_bounds() !== null. */
  mesh: boolean;
  /** ft_ground_occupancy_height_ft(). */
  height: number;
  squeezeSizeFt?: number;
}
export interface CrossingRouteInput {
  /** ft_encounter_shape() as the scene holds it (live door states) — null shape = shapeless (the bounded open plane). */
  world: RouteWallWorld | { shape: null; canvasExterior: boolean; overlayUnknown: boolean };
  from: readonly [number, number];
  to: readonly [number, number];
  /** The mover's persisted feet z (pos_coord z_pos). */
  z: number;
  body: CrossingRouteBody;
  /** Non-mesh creature movement uses the same size-category floors in every mode. */
  allowSqueeze: boolean;
  /** Every other creature row the router sees: not the mover / its compound, not private, not a hidden opposing row. `tall` = a corpse taller
   *  than the mover's step (ft3d_corpse_support_rows) — a HARD disc as well as a weighted one. */
  creatures: ReadonlyArray<{ id: string; x: number; y: number; z: number; r: number; widthFt?: number; dead: boolean; tall?: boolean }>;
  /** The solid props (walk-route-obstacles.ts): `banded` = floor-band filtered (a painted-wall part is not). */
  hard: ReadonlyArray<CrossingHardObstacle>;
  /** encounterDifficultFootprints(encounter). */
  footprints: readonly unknown[];
  climate?: DifficultTerrainClimateContext;
}
export type CrossingRoute = { kind: 'route'; route: Array<[number, number]> }
  | { kind: 'handback'; why: string; detail?: Record<string, unknown> };
/** One of ft_route_encounter_move()'s $hard_obstacles: a disc (`physical` null — x / y / r) or a physical zone (a mesh footprint, a wall part). */
export interface CrossingHardObstacle { id: string; x: number; y: number; z: number; r: number; physical: Zone | null; banded: boolean }
/** MV6: what the page can say about the router's hard set (websocket/walk-route-obstacles.ts): the solid props for the mover's z span and every
 *  corpse's support height (null = carried, NaN = the page cannot prove it lying or carried) — or why it cannot build it. */
export type WalkRouteObstacles = { props: CrossingHardObstacle[]; corpseHeights: ReadonlyMap<string, number | null> } | { uncertain: string; inventory?: string };
export interface WalkRouteMover {
  /** Creature rules tier, never mesh width; null for a dragged inventory member. */
  tierFt: number | null;
  compoundIds: ReadonlySet<string>; zMin: number; zMax: number; searchRegion?: CrossingSearchRegion; landing?: boolean;
}

/** The router's entire search plane, not just the submitted route: A* may detour anywhere inside it. */
export interface CrossingSearchRegion { plane: RestShape; structural: boolean; rayZ: number; radius: number }
export function crossingSearchRegion(d: Pick<CrossingRouteInput, 'world' | 'from' | 'to' | 'z' | 'body'>): CrossingSearchRegion {
  const [fx, fy] = d.from, [tx, ty] = d.to, r = d.body.radius, margin = 40 + r * 2;
  const shape = d.world.shape as RestShape | null;
  const band = shape && Array.isArray(shape.boxes) ? floorBandShape(shape, fx, fy, d.z, d.world.canvasExterior) : null;
  let structural = band ? withExteriorOpenGround(band.shape, fx, fy, tx, ty, margin, d.world.canvasExterior) : null;
  if (structural && d.body.mesh) structural = physicalBodyNavigationShape(structural as never, d.body.height) as unknown as RestShape;
  return { structural: structural !== null, rayZ: d.z + d.body.height / 2, radius: r,
    plane: structural ?? { v: 1, unit: 'ft', boxes: [{ id: 'open',
      x: { min: Math.min(fx, tx) - margin, max: Math.max(fx, tx) + margin },
      y: { min: Math.min(fy, ty) - margin, max: Math.max(fy, ty) + margin }, floor: 0, ceiling: 100 }], openings: [] } };
}
/** A bounding disc includes rotated oblongs. Unknown geometry is unbounded; edge contact stays relevant. */
export function crossingRegionMayTouch(region: CrossingSearchRegion | undefined, x: number, y: number, reach: number): boolean {
  if (!region || ![x, y, reach].every(Number.isFinite) || reach < 0) return true;
  return region.plane.boxes.some(box => Math.hypot(Math.max(box.x.min - x, 0, x - box.x.max),
    Math.max(box.y.min - y, 0, y - box.y.max)) <= reach + region.radius + 1e-6);
}

/** ft_route_encounter_move()'s $passable_at: outside every hard obstacle — or, for one the mover STARTS inside, no deeper than the start. */
export function hardPassableAt(hard: ReadonlyArray<Pick<CrossingHardObstacle, 'x' | 'y' | 'r' | 'physical'>>, from: readonly [number, number], radius: number,
  x: number, y: number, footprintAt?: (x: number, y: number) => Zone): boolean {
  const [fx, fy] = from;
  for (const o of hard) {
    if (o.physical || footprintAt) {
      const zone = o.physical ? { ...o.physical, zMin: 0, zMax: 1 } : circleOccupancy(o.x, o.y, o.r, 0, 1);
      const moverAt = (px: number, py: number) => footprintAt ? { ...footprintAt(px, py), zMin: 0, zMax: 1 } : circleOccupancy(px, py, radius, 0, 1);
      if (!occupancyOverlap(moverAt(x, y), zone)) continue;
      if (occupancyOverlap(moverAt(fx, fy), zone)
        && Math.hypot(x - o.x, y - o.y) >= Math.hypot(fx - o.x, fy - o.y) - 1e-6) continue;
      return false;
    }
    const clearance = o.r + radius;
    const distance = Math.hypot(x - o.x, y - o.y);
    if (distance >= clearance) continue;
    const start = Math.hypot(fx - o.x, fy - o.y);
    if (start < clearance && distance >= start - 1e-6) continue;
    return false;
  }
  return true;
}

export function routeCrossingWalk(d: CrossingRouteInput): CrossingRoute {
  const [fx, fy] = d.from, [tx, ty] = d.to;
  const r = d.body.radius;
  const mover = { diameter: d.body.diameter ?? r * 2, height: d.body.height, widthFt: d.body.widthFt };
  const footprintAt = d.body.widthFt === undefined ? undefined : (x: number, y: number): Zone => movementFootprint(mover, x, y, d.z);
  if (![fx, fy, tx, ty, d.z, r].every(Number.isFinite) || !(r > 0)) return { kind: 'handback', why: 'crossing-input' };
  // ft_route_encounter_move(): a no-op leg is no route (the endpoint walks the straight leg).
  if (Math.hypot(tx - fx, ty - fy) < 0.01) return { kind: 'route', route: [[fx, fy], [tx, ty]] };
  const shape = d.world.shape as RestShape | null;
  const shaped = !!shape && Array.isArray(shape.boxes);
  // $on_mover_floor: rows on the mover's floor band (ft_shape_floor_band_for_position); every row when the encounter has no shape.
  const bandFloor = (x: number, y: number, z: number): number => floorBandShape(shape!, x, y, z)?.floor ?? 0;
  const moverFloor = shaped ? bandFloor(fx, fy, d.z) : null;
  const onMoverFloor = (x: number, y: number, z: number): boolean => moverFloor === null || Math.abs(bandFloor(x, y, z) - moverFloor) <= 0.01;
  const creatures = d.creatures.filter(c => onMoverFloor(c.x, c.y, c.z));
  // $hard_obstacles: ft3d_corpse_support_rows() (a corpse taller than the step, on the band) as discs, then the solid props — a painted-wall
  // part whatever its band (PHP reads the wall bindings before its floor-band test), every other prop on the mover's band.
  const hard: Array<Pick<CrossingHardObstacle, 'x' | 'y' | 'r' | 'physical'>> = [
    ...creatures.filter(c => c.dead && c.tall).map(c => ({ x: c.x, y: c.y, r: c.r,
      physical: c.widthFt === undefined ? null : movementFootprint({ diameter: c.r * 2, widthFt: c.widthFt, height: 1 }, c.x, c.y, d.z) })),
    ...d.hard.filter(p => !p.banded || onMoverFloor(p.x, p.y, p.z)),
  ];
  if (hard.some(o => ![o.x, o.y, o.r].every(Number.isFinite))) return { kind: 'handback', why: 'crossing-hard-geometry' };
  // $difficult_footprints, kept to the mover's elevation band (stepIsDifficultTerrain applies the same band test per point).
  const footprints = d.footprints;
  const climateActive = !!d.climate && !d.climate.ignoresTerrain
    && (!!d.climate.zones?.length || (!d.climate.ignoresSnowAndIce && movementClimateGround(d.climate).active));
  const difficultAt = (x: number, y: number): boolean => stepIsDifficultTerrain(x, y, d.z, r, footprints, d.climate);

  const { plane, structural, rayZ } = crossingSearchRegion(d);
  if (structural && d.world.overlayUnknown && (plane.openings?.length ?? 0) > 0) return { kind: 'handback', why: 'crossing-overlay' };
  if (creatures.length === 0 && hard.length === 0 && footprints.length === 0 && !climateActive && !structural) return { kind: 'route', route: [[fx, fy], [tx, ty]] };
  const costAt = (x: number, y: number): number => {
    for (const other of creatures) if (mover.widthFt === undefined && other.widthFt === undefined
      ? Math.hypot(x - other.x, y - other.y) < other.r + r
      : movementOverlap({ ...mover, height: 1 }, { x, y, z: 0 },
        { diameter: other.r * 2, widthFt: other.widthFt, height: 1 }, { x: other.x, y: other.y, z: 0 })) return 2;
    return difficultAt(x, y) ? 2 : 1;
  };
  const walls = footprintAt && structural ? routeWallReader(d.world, { ...mover, baseFt: mover.diameter,
    mesh: d.body.mesh, allowSqueeze: d.allowSqueeze, squeezeSizeFt: d.body.squeezeSizeFt }, { x: fx, y: fy, z: d.z }) : null;
  const options = {
    rayZ, stepFt: 1,
    costMultiplierAt: creatures.length === 0 && footprints.length === 0 && !climateActive ? null : costAt,
    heightFt: d.body.mesh ? d.body.height : 0,
    passableAt: hard.length === 0 && !walls ? null : (x: number, y: number): boolean => (!walls || walls(x, y, d.z) === true)
      && hardPassableAt(hard, [fx, fy], r, x, y, footprintAt),
    allowSqueeze: d.allowSqueeze && !d.body.mesh,
    squeezeSizeFt: d.body.squeezeSizeFt,
  };
  type Attempt = { route: { polyline: Array<[number, number]> } | null; reason: string | null };
  const clearanceFt = structural && !footprintAt ? r : 0;
  let attempt = routeBetween(plane as never, [fx, fy], [tx, ty], { ...options, cellFt: 5, clearanceFt, maxExpansions: 8000 }) as Attempt;
  if (!attempt.route && structural && (d.allowSqueeze || attempt.reason !== ROUTE_NO_PATH)) {
    attempt = routeBetween(plane as never, [fx, fy], [tx, ty], { ...options, cellFt: 2.5, clearanceFt: footprintAt ? 0 : r, maxExpansions: 24000 }) as Attempt;
  }
  const polyline = attempt.route?.polyline;
  if (!polyline || polyline.length < 2) {
    // PHP: a shaped encounter refuses ("No walkable route"); an unshaped one walks the straight leg — either way the legacy endpoint says it.
    return { kind: 'handback', why: structural ? 'crossing-no-route' : 'crossing-open-plane-no-route', detail: { reason: attempt.reason ?? null } };
  }
  return { kind: 'route', route: polyline.map(p => [Number(p[0]), Number(p[1])] as [number, number]) };
}
